import React from 'react';
import { CheckCircleIcon, XIcon } from '../../components/ui/Icons';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Approval, ApprovalStep } from './useData';

function groupStepsByStage(steps: ApprovalStep[]) {
  const order: number[] = [];
  const map = new Map<number, ApprovalStep[]>();
  steps.forEach((step) => {
    if (!map.has(step.stage)) {
      map.set(step.stage, []);
      order.push(step.stage);
    }
    map.get(step.stage)!.push(step);
  });
  return order.map((stage) => ({ stage, steps: map.get(stage)! }));
}

const displayUser = (
  user?: { userDisplayName?: string | null; userFirstName: string; userLastName: string } | null,
) => user?.userDisplayName || [user?.userFirstName, user?.userLastName].filter(Boolean).join(' ') || null;

function formatDueAt(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const FlowArrowRight: React.FC<{ label?: string }> = ({ label }) => (
  <div className="flex shrink-0 items-center gap-1.5 px-1" aria-hidden>
    {label ? (
      <>
        <svg width="18" height="14" viewBox="0 0 18 14" className="text-slate-400 dark:text-slate-500">
          <line x1="0" y1="7" x2="10" y2="7" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400">
          {label}
        </span>
        <svg width="18" height="14" viewBox="0 0 18 14" className="text-slate-400 dark:text-slate-500">
          <line x1="0" y1="7" x2="10" y2="7" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M8 3.5 L14 7 L8 10.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </>
    ) : (
      <svg width="28" height="14" viewBox="0 0 28 14" className="text-slate-400 dark:text-slate-500">
        <line x1="0" y1="7" x2="18" y2="7" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M16 3.5 L22 7 L16 10.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )}
  </div>
);

const stepStatusStyles: Record<
  ApprovalStep['status'],
  { ring: string; badge: string; badgeText: string }
> = {
  PENDING: {
    ring: 'ring-1 ring-amber-200/80 dark:ring-amber-800',
    badge: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    badgeText: 'Pending',
  },
  APPROVED: {
    ring: 'ring-1 ring-emerald-200/80 dark:ring-emerald-800',
    badge: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
    badgeText: 'Approved',
  },
  REJECTED: {
    ring: 'ring-1 ring-red-200/80 dark:ring-red-800',
    badge: 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300',
    badgeText: 'Rejected',
  },
  SKIPPED: {
    ring: 'ring-1 ring-slate-200/80 dark:ring-slate-700',
    badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    badgeText: 'Skipped',
  },
};

export type ApprovalFlowAction = 'approve' | 'reject';

export const ApprovalFlowPreview: React.FC<{
  approval: Approval;
  className?: string;
  /** Wider layout for the approval detail page */
  layout?: 'compact' | 'wide';
  busy?: boolean;
  isActionable?: (step: ApprovalStep) => boolean;
  /** When the current user is acting via delegation, return the original assignee's display name. */
  actingForName?: (step: ApprovalStep) => string | null;
  onAct?: (stepId: number, action: ApprovalFlowAction) => void;
}> = ({ approval, className, layout = 'compact', busy = false, isActionable, actingForName, onAct }) => {
  const { colors } = useTheme();
  const wide = layout === 'wide';
  const steps = [...(approval.steps || [])].sort(
    (a, b) => a.stage - b.stage || a.sequence - b.sequence || a.id - b.id,
  );
  const groups = groupStepsByStage(steps);

  const assigneeName = (step: ApprovalStep) =>
    displayUser(step.assigneeUser) ||
    step.assigneeDepartment?.name ||
    step.assigneeDepartment?.departmentName ||
    'Unassigned';

  const outcomeApproved = approval.status === 'APPROVED';
  const outcomeRejected = approval.status === 'REJECTED';
  const outcomePending = approval.status === 'PENDING' || approval.status === 'DRAFT';

  return (
    <div className={cn('overflow-hidden rounded-2xl border shadow-sm', colors.surface, colors.border, className)}>
      <div className={cn('border-b', colors.border, wide ? 'px-6 py-4' : 'px-4 py-3')}>
        <p className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>
          {wide ? 'Approval flow' : 'Flow preview'}
        </p>
        <p className={cn('mt-0.5 truncate font-semibold', colors.text, wide ? 'text-base' : 'text-sm')}>
          {approval.title}
        </p>
        {wide && (
          <p className={cn('mt-1 text-sm', colors.textMuted)}>
            Stages run left to right — a later stage unlocks only after the previous stage is finished. Steps in the
            same stage can approve in parallel.
          </p>
        )}
      </div>
      <div className={cn(wide ? 'px-6 py-6' : 'px-4 py-5')}>
        {groups.length === 0 ? (
          <div className={cn('space-y-2 py-6 text-center text-sm', colors.textMuted)}>
            <p>No workflow steps yet</p>
            {approval.status === 'DRAFT' && (
              <p>
                This request is in Draft
                {approval.payload &&
                typeof approval.payload === 'object' &&
                !Array.isArray(approval.payload) &&
                approval.payload._lastReturn
                  ? ' after being returned for changes. Review Activity & comments below, then resubmit.'
                  : '. Submit it to create workflow steps from the approval template.'}
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto pb-1">
            <div className="flex min-w-min items-stretch gap-0">
              <div className="flex shrink-0 items-center">
                <div
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide shadow-sm',
                    'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800',
                  )}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Start
                </div>
              </div>

              {groups.map((group, groupIndex) => {
                const isParallel = group.steps.length > 1;
                const stageMode = group.steps[0]?.stageMode === 'ANY' ? 'ANY' : 'ALL';
                return (
                  <React.Fragment key={group.stage}>
                    <div className="flex shrink-0 items-center">
                      <FlowArrowRight label={groupIndex === 0 ? undefined : 'Then'} />
                    </div>

                    <div
                      className={cn(
                        'flex w-[min(100%,280px)] shrink-0 flex-col',
                        wide ? 'min-w-[240px]' : 'min-w-[200px]',
                      )}
                    >
                      <div className="mb-2 flex items-center justify-center gap-2">
                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-slate-800 px-1.5 text-[10px] font-bold text-white dark:bg-slate-200 dark:text-slate-900">
                          {groupIndex + 1}
                        </span>
                        <span className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                          Stage {groupIndex + 1}
                        </span>
                      </div>

                      {isParallel && (
                        <div className="mb-2 flex justify-center">
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                              stageMode === 'ANY'
                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                                : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
                            )}
                          >
                            {stageMode === 'ANY' ? 'Any one can approve' : 'All must approve'}
                          </span>
                        </div>
                      )}

                      <div className="flex flex-1 flex-col gap-2">
                        {group.steps.map((step, stepInStage) => {
                          const statusStyle = stepStatusStyles[step.status] || stepStatusStyles.PENDING;
                          const canAct = !!isActionable?.(step) && !!onAct;
                          const waitingOnEarlier =
                            step.status === 'PENDING' &&
                            steps.some((s) => s.stage < step.stage && s.status === 'PENDING');
                          const isOverdue =
                            step.status === 'PENDING' && !!step.dueAt && new Date(step.dueAt).getTime() < Date.now();
                          return (
                            <div
                              key={step.id}
                              className={cn(
                                'rounded-xl border text-center shadow-sm',
                                colors.border,
                                colors.surface,
                                isOverdue ? 'ring-1 ring-red-300 dark:ring-red-800' : statusStyle.ring,
                                waitingOnEarlier && 'opacity-70',
                                wide ? 'px-4 py-3.5' : 'px-3 py-2.5',
                              )}
                            >
                              {isParallel && (
                                <p className={cn('mb-1 text-[9px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                                  {stageMode === 'ANY' ? `Option ${stepInStage + 1}` : `Approver ${stepInStage + 1}`}
                                </p>
                              )}
                              <p className={cn('font-semibold', colors.text, wide ? 'text-sm' : 'text-xs')}>
                                {step.label || 'Step'}
                              </p>
                              <p className={cn('mt-0.5 truncate', colors.textMuted, wide ? 'text-xs' : 'text-[11px]')}>
                                {assigneeName(step)}
                              </p>
                              {step.status === 'PENDING' && step.dueAt && formatDueAt(step.dueAt) && (
                                <p
                                  className={cn(
                                    'mt-1 text-[10px] font-medium',
                                    isOverdue ? 'text-red-600 dark:text-red-400' : colors.textMuted,
                                  )}
                                >
                                  {isOverdue ? 'Overdue since ' : 'Due '}
                                  {formatDueAt(step.dueAt)}
                                </p>
                              )}
                              {step.actedAt && (
                                <p className={cn('mt-1 text-[10px]', colors.textMuted)}>
                                  {new Date(step.actedAt).toLocaleString()}
                                </p>
                              )}
                              {step.comments && (
                                <p className={cn('mt-2 text-left text-xs italic', colors.textMuted)}>"{step.comments}"</p>
                              )}
                              <div className="mt-2 flex flex-wrap justify-center gap-1">
                                <span className={cn('rounded-full px-1.5 py-0.5 text-[9px] font-semibold', statusStyle.badge)}>
                                  {statusStyle.badgeText}
                                </span>
                                {isOverdue && (
                                  <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[9px] font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300">
                                    Overdue
                                  </span>
                                )}
                                {step.status === 'PENDING' && (
                                  <>
                                    <span className="inline-flex rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                      {step.onApprove === 'COMPLETE' ? '→ Done' : '→ Next'}
                                    </span>
                                    <span
                                      className={cn(
                                        'inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-semibold',
                                        step.onReject === 'RETURN'
                                          ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                                          : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
                                      )}
                                    >
                                      {step.onReject === 'RETURN' ? '→ Draft' : '→ Reject'}
                                    </span>
                                  </>
                                )}
                              </div>
                              {canAct && (
                                <div className="mt-3 space-y-2">
                                  {(() => {
                                    const forName =
                                      step.actingFor
                                        ? displayUser(step.actingFor)
                                        : actingForName?.(step) || null;
                                    return forName ? (
                                      <p className="rounded-lg bg-indigo-50 px-2 py-1 text-left text-[11px] font-medium text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200">
                                        Acting for {forName}
                                      </p>
                                    ) : null;
                                  })()}
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      disabled={busy}
                                      onClick={() => onAct(step.id, 'approve')}
                                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                                    >
                                      <CheckCircleIcon className="h-4 w-4" />
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      disabled={busy}
                                      onClick={() => onAct(step.id, 'reject')}
                                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-red-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                                    >
                                      <XIcon className="h-4 w-4" />
                                      {step.onReject === 'RETURN' ? 'Return' : 'Reject'}
                                    </button>
                                  </div>
                                  <p className={cn('text-left text-[11px]', colors.textMuted)}>
                                    {step.onReject === 'RETURN'
                                      ? 'Reject returns this request to Draft.'
                                      : 'Reject is final.'}
                                    {step.onApprove === 'COMPLETE'
                                      ? ' Approve completes the workflow.'
                                      : ' Approve continues when this stage is done.'}
                                  </p>
                                </div>
                              )}
                              {!canAct && waitingOnEarlier && (
                                <p className={cn('mt-3 text-left text-[11px] font-medium', colors.textMuted)}>
                                  Waiting for stage {Math.max(...steps.filter((s) => s.stage < step.stage).map((s) => s.stage))} to
                                  finish before approve / reject is available.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              <div className="flex shrink-0 items-center">
                <FlowArrowRight />
              </div>

              <div className="flex w-[200px] shrink-0 flex-col justify-center gap-2">
                <div
                  className={cn(
                    'mx-auto w-fit rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide',
                    colors.border,
                    colors.text,
                  )}
                >
                  Outcomes
                </div>
                <div className="flex flex-col gap-2">
                  <div
                    className={cn(
                      'rounded-xl border px-3 py-2.5 text-center text-[11px] font-semibold',
                      outcomeApproved
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                        : cn(colors.border, colors.textMuted, outcomePending && 'opacity-70'),
                    )}
                  >
                    Approved
                  </div>
                  <div
                    className={cn(
                      'rounded-xl border px-3 py-2.5 text-center text-[11px] font-semibold',
                      outcomeRejected
                        ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300'
                        : cn(colors.border, colors.textMuted, outcomePending && 'opacity-70'),
                    )}
                  >
                    Rejected
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
